import qrcode from "qrcode-generator"
import BrowserPrint from "./BrowserPrint"
import _ from "lodash"

// const format_start = "^XA^LL200^FO80,50^A0N36,36^FD"
// const format_end = "^FS^XZ"

type BrowserPrintDevice = {
  name: string
  deviceType: string
  connection: string
  uid: string
  version: number
  provider?: string
  manufacturer?: string
  send: (data: string, onOk?: (t: string) => void, onErr?: (e: any) => void) => void
  sendUrl: (url: string, onOk?: (t: string) => void, onErr?: (e: any) => void) => void
  read: (onOk?: (t: string) => void, onErr?: (e: any) => void) => void
  sendThenRead: (data: string, onOk?: (t: string) => void, onErr?: (e: any) => void) => void
}

export const getDefaultPrinter = (): Promise<BrowserPrintDevice> =>
  new Promise((resolve, reject) => {
    BrowserPrint.getDefaultDevice(
      "printer",
      (device: BrowserPrintDevice | null) => {
        if (!device) return reject(new Error("No default printer found"))
        console.log("Printer found:", device.name)
        resolve(device)
      },
      (error: any) => reject(error ?? new Error("Failed to get default printer"))
    )
  })

enum PrinterStatus {
  ReadyToPrint = "Ready to Print",
  PaperOut = "Paper out",
  RibbonOut = "Ribbon Out",
  MediaDoorOpen = "Media Door Open",
  CutterFault = "Cutter Fault",
  PrintheadOverheating = "Printhead Overheating",
  MotorOverheating = "Motor Overheating",
  PrintheadFault = "Printhead Fault",
  IncorrectPrinthead = "Incorrect Printhead",
  PrinterPaused = "Printer Paused",
  UnknownError = "Error: Unknown Error",
}

function sendThenReadP(device: BrowserPrintDevice, data: string, timeoutMs = 8000): Promise<string> {
  return new Promise((resolve, reject) => {
    let done = false
    const t = setTimeout(() => {
      if (done) return
      done = true
      reject(new Error("Status read timed out"))
    }, timeoutMs)

    device.sendThenRead(
      data,
      (text: string) => {
        if (done) return
        done = true
        clearTimeout(t)
        resolve(text ?? "")
      },
      (err: any) => {
        if (done) return
        done = true
        clearTimeout(t)
        reject(err ?? new Error("sendThenRead failed"))
      }
    )
  })
}

function parseHqes(text: string): PrinterStatus {
  // Defensive guards: we index specific positions; make sure we can.
  // (Zebra ~HQES returns a fixed-format multi-line string)
  const safeChar = (i: number) => (i >= 0 && i < text.length ? text.charAt(i) : "")

  const is_error = safeChar(70) // "0" means no error
  const media = safeChar(88) // paper/ribbon/door/cutter
  const head = safeChar(87) // head/motor errors
  const pause = safeChar(84) // pause state

  if (is_error === "0") return PrinterStatus.ReadyToPrint

  if (media === "1") return PrinterStatus.PaperOut
  if (media === "2") return PrinterStatus.RibbonOut
  if (media === "4") return PrinterStatus.MediaDoorOpen
  if (media === "8") return PrinterStatus.CutterFault

  if (head === "1") return PrinterStatus.PrintheadOverheating
  if (head === "2") return PrinterStatus.MotorOverheating
  if (head === "4") return PrinterStatus.PrintheadFault
  if (head === "8") return PrinterStatus.IncorrectPrinthead

  if (pause === "1") return PrinterStatus.PrinterPaused

  return PrinterStatus.UnknownError
}

async function checkPrinterStatus(printer: BrowserPrintDevice): Promise<PrinterStatus> {
  // ~HQES = Host Query: Extended Status
  const raw = await sendThenReadP(printer, "~HQES")
  if (!raw || raw.length < 90) {
    // Fallback: if response is too short or malformed
    return PrinterStatus.UnknownError
  }
  console.log(raw)
  return parseHqes(raw)
}

function mmToDots(mm: number, dpi = 203) {
  return Math.round((mm / 25.4) * dpi)
}

// Choose magnification given the module count
function chooseMagnificationByModules(modules: number, maxSizeDots: number, minMag = 2, maxMag = 12) {
  const mag = Math.floor(maxSizeDots / (modules + 8)) // include quiet zone
  return Math.max(minMag, Math.min(maxMag, mag))
}

function getQrModulesCount(data: string) {
  // 0 = automatic version selection
  const qr = qrcode(0, "M")
  // choose mode: "Byte" is safest for URLs / UTF-8
  qr.addData(data, "Byte")
  qr.make()
  return qr.getModuleCount() // number of modules (e.g. 21..177)
}

function computeMagnification(data: string, boxSizeMm: number, dpi: number) {
  const modules = getQrModulesCount(data)
  const maxDots = mmToDots(boxSizeMm, dpi)
  return chooseMagnificationByModules(modules, maxDots)
}

export const printQRCode = async (data: string, copies: number, textPart?: string) => {
  try {
    const size = 15 // mm
    const magnification = computeMagnification(data, size, 300)

    const textParts = textPart ? _.chunk(textPart, 8) : []
    const zpl =
      "^XA\n" +
      "^CI28\n" +
      `^PW1000\n` +
      "^MD24\n" +
      "^LH0,0\n" +
      // QR codes
      "^FO20,40\n" +
      `^BQN,2,${magnification}\n` + // Default 10
      "^FDLA," +
      data +
      "^FS\n" +
      // Text
      textParts
        .map(
          (part, i) =>
            `^FO${mmToDots(size, 300) * 0.75},${50 + i * 20}\n^A0N,16,16\n^FB360,5,0,L,0\n^FD${part.join("")}^FS\n`
        )
        .join("") +
      // Copies
      `^PQ${copies}\n` +
      // End
      "^XZ"

    console.log(zpl)

    console.log("Get default printer")
    const printer = await getDefaultPrinter()

    console.log("Get printer status")
    const status = await checkPrinterStatus(printer)
    console.log({ status })

    if (status === PrinterStatus.ReadyToPrint) {
      console.log("Print")
      await new Promise<void>((resolve, reject) => {
        printer.send(
          `${zpl}`,
          () => resolve(),
          (e: any) => reject(e ?? new Error("Print failed"))
        )
      })
    } else {
      alert(`Failed to print: ${status}`)
    }
  } catch (err) {
    console.error("Failed to print", err)
    alert(`Failed to print: ${err instanceof Error ? err.message : String(err)}`)
  }
}
